import React, { useState } from 'react';
import {
  Modal,
  Checkbox,
  Radio,
  Alert,
  Space,
  Typography,
  Divider,
  Progress,
  Result,
  Button,
} from 'antd';
import {
  DownloadOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { ExportModalProps, ExportConfig } from '../types';
import { DesignTokens } from '../DesignTokens';

const { Text, Title } = Typography;

export const ExportModal: React.FC<ExportModalProps> = ({
  open,
  currentFilters,
  totalCount,
  onCancel,
  onExport,
}) => {
  const [selectedFields, setSelectedFields] = useState<string[]>([
    'username',
    'realName',
    'phone',
    'email',
    'roles',
    'department',
    'status',
  ]);
  const [format, setFormat] = useState<'excel' | 'csv'>('excel');
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [exported, setExported] = useState(false);

  const availableFields = [
    { label: '用户名', value: 'username' },
    { label: '真实姓名', value: 'realName' },
    { label: '手机号', value: 'phone' },
    { label: '邮箱', value: 'email' },
    { label: '角色', value: 'roles' },
    { label: '部门', value: 'department' },
    { label: '状态', value: 'status' },
    { label: '创建时间', value: 'createdAt' },
    { label: '最后登录', value: 'lastLogin' },
  ];

  const handleSelectAll = () => {
    if (selectedFields.length === availableFields.length) {
      setSelectedFields([]);
    } else {
      setSelectedFields(availableFields.map((f) => f.value));
    }
  };

  const handleExport = async () => {
    if (!onExport || selectedFields.length === 0) return;

    setExporting(true);
    setProgress(0);

    // 模拟进度
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + 10;
      });
    }, 200);

    try {
      const config: ExportConfig = {
        fields: selectedFields,
        format,
        filters: currentFilters,
      };

      await onExport(config);
      clearInterval(progressInterval);
      setProgress(100);
      setExported(true);
    } catch (error) {
      clearInterval(progressInterval);
      console.error('导出失败:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleClose = () => {
    setExported(false);
    setProgress(0);
    onCancel?.();
  };

  const renderContent = () => {
    if (exported) {
      return (
        <Result
          status="success"
          title="导出成功"
          subTitle={`已导出 ${totalCount} 条用户数据`}
          icon={
            <CheckCircleOutlined
              style={{ color: DesignTokens.colors.success }}
            />
          }
          extra={[
            <Button
              key="download"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleClose}
            >
              下载文件
            </Button>,
          ]}
        />
      );
    }

    if (exporting) {
      return (
        <Space
          direction="vertical"
          size="large"
          style={{ width: '100%', padding: '40px 0', textAlign: 'center' }}
        >
          <Title level={4}>正在导出数据...</Title>
          <Progress
            type="circle"
            percent={progress}
            strokeColor={DesignTokens.colors.primary}
          />
        </Space>
      );
    }

    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Alert
          message={`将导出 ${totalCount} 条用户数据`}
          type="info"
          showIcon
        />

        {currentFilters && Object.keys(currentFilters).length > 0 && (
          <Alert
            message="当前筛选条件"
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {currentFilters.status && (
                  <li>状态: {currentFilters.status === 'active' ? '正常' : '禁用'}</li>
                )}
                {currentFilters.keyword && (
                  <li>关键字: {currentFilters.keyword}</li>
                )}
              </ul>
            }
            type="warning"
            showIcon
          />
        )}

        <Divider orientation="left">选择导出字段</Divider>

        <div>
          <Checkbox
            indeterminate={
              selectedFields.length > 0 &&
              selectedFields.length < availableFields.length
            }
            checked={selectedFields.length === availableFields.length}
            onChange={handleSelectAll}
            style={{ marginBottom: 12 }}
          >
            全选
          </Checkbox>
          <Checkbox.Group
            options={availableFields}
            value={selectedFields}
            onChange={(values) => setSelectedFields(values as string[])}
            style={{ width: '100%' }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 12,
              }}
            >
              {availableFields.map((field) => (
                <Checkbox key={field.value} value={field.value}>
                  {field.label}
                </Checkbox>
              ))}
            </div>
          </Checkbox.Group>
        </div>

        <Divider orientation="left">选择导出格式</Divider>

        <Radio.Group
          value={format}
          onChange={(e) => setFormat(e.target.value)}
        >
          <Space direction="vertical">
            <Radio value="excel">
              Excel (.xlsx) - 推荐，支持格式化
            </Radio>
            <Radio value="csv">
              CSV (.csv) - 纯文本，兼容性好
            </Radio>
          </Space>
        </Radio.Group>

        {selectedFields.length === 0 && (
          <Alert
            message="请至少选择一个导出字段"
            type="warning"
            showIcon
          />
        )}
      </Space>
    );
  };

  return (
    <Modal
      title="批量导出用户"
      open={open}
      onCancel={handleClose}
      footer={
        exported || exporting
          ? null
          : [
              <Button key="cancel" onClick={handleClose}>
                取消
              </Button>,
              <Button
                key="export"
                type="primary"
                disabled={selectedFields.length === 0}
                loading={exporting}
                onClick={handleExport}
                icon={<DownloadOutlined />}
              >
                导出
              </Button>,
            ]
      }
      width={640}
      closable={!exporting}
      maskClosable={!exporting}
      styles={{
        header: {
          background: DesignTokens.colors.gray2,
          padding: `${DesignTokens.spacing.lg}px ${DesignTokens.spacing.xl}px`,
        },
        body: {
          padding: DesignTokens.spacing.xl,
        },
      }}
    >
      {renderContent()}
    </Modal>
  );
};
